/**
 * Support Admin Service — Org Admin account-lane queue (`/support/admin/*`).
 * See docs/analysis/support-help-center-plan.md Part 2. Every list/detail call
 * is server-anchored to the caller's organizationId — this layer just passes
 * filters through.
 */

import apiClient from './apiClient';
import { downloadSupportAttachment } from './supportAttachmentDownload';
import type {
  SupportRequestDetail,
  SupportRequestSummary,
  SupportRequestStatus,
  SupportRequestType,
  SupportReplyInput,
  UpdateSupportRequestInput,
  SupportAssignableHandler,
  SupportRequesterOption,
} from '@upskill/shared';

const BASE = '/api/v1/support/admin';

const bgConfig = { _backgroundRequest: true } as any;

export interface ListSupportQueueParams {
  page?: number;
  limit?: number;
  status?: SupportRequestStatus;
  requesterId?: string;
  type?: SupportRequestType;
}

/**
 * `SupportRequestSummary.organization` (id + name) is populated only by the
 * platform queue — the admin queue's rows just never set it (single-tenant,
 * no join). One shared row/list shape for both queues, so the hook factory
 * (useSupportQueue.ts) stays single-generic.
 */
export interface PaginatedSupportRequests {
  data: SupportRequestSummary[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

interface PaginatedEnvelope extends PaginatedSupportRequests {
  success: boolean;
}

interface UnreadCountEnvelope {
  success: boolean;
  data: { count: number };
}

interface DetailEnvelope {
  success: boolean;
  data: SupportRequestDetail;
}

export const listRequests = async (
  params: ListSupportQueueParams = {},
): Promise<PaginatedSupportRequests> => {
  const response = await apiClient.get<PaginatedEnvelope>(`${BASE}/requests`, { params });
  const { success: _success, ...paginated } = response.data;
  return paginated;
};

export const getUnreadCount = async (): Promise<number> => {
  const response = await apiClient.get<UnreadCountEnvelope>(
    `${BASE}/requests/unread-count`,
    bgConfig,
  );
  return response.data.data.count;
};

export const getRequestDetail = async (id: string): Promise<SupportRequestDetail> => {
  const response = await apiClient.get<DetailEnvelope>(`${BASE}/requests/${id}`);
  return response.data.data;
};

export const replyToRequest = async (
  id: string,
  input: SupportReplyInput,
): Promise<SupportRequestDetail> => {
  const response = await apiClient.post<DetailEnvelope>(`${BASE}/requests/${id}/messages`, input);
  return response.data.data;
};

export const updateRequest = async (
  id: string,
  input: UpdateSupportRequestInput,
): Promise<SupportRequestDetail> => {
  const response = await apiClient.patch<DetailEnvelope>(`${BASE}/requests/${id}`, input);
  return response.data.data;
};

/** ACCOUNT -> PLATFORM: hands the thread to UpskillABA support. */
export const escalateRequest = async (id: string): Promise<SupportRequestDetail> => {
  const response = await apiClient.post<DetailEnvelope>(`${BASE}/requests/${id}/escalate`);
  return response.data.data;
};

export const downloadAttachment = downloadSupportAttachment;

/** Handlers eligible to be reassigned a request — org members holding SUPPORT_REQUESTS_MANAGE. */
export const listAssignableHandlers = async (): Promise<SupportAssignableHandler[]> => {
  const response = await apiClient.get<{ success: boolean; data: SupportAssignableHandler[] }>(
    `${BASE}/assignable-handlers`,
  );
  return response.data.data;
};

/** Distinct requesters in this queue's scope — feeds the requester filter. */
export const listRequesters = async (): Promise<SupportRequesterOption[]> => {
  const response = await apiClient.get<{ success: boolean; data: SupportRequesterOption[] }>(
    `${BASE}/requesters`,
  );
  return response.data.data;
};

const supportAdminService = {
  listRequests,
  getUnreadCount,
  getRequestDetail,
  replyToRequest,
  updateRequest,
  escalateRequest,
  downloadAttachment,
  listAssignableHandlers,
  listRequesters,
};

export default supportAdminService;
